import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import React from "react";
import { toast } from "react-toastify";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { UploadPanel } from "~/components/common/upload/UploadPanel";
import useLoader from "~/hooks/useLoader";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type { ImportType } from "~/models/ImportLog";
import { isSuccess } from "~/utils/api/api_handler";
import { create } from "~/utils/api/ImportLog";

type Props = DeepReadonly<{
	handleMutate: () => Promise<void>;
	importType: ImportType;
}>;

export const ImportFileUploadPanel: React.FC<Props> = ({
	importType,
	handleMutate
}: Props) => {
	const { Loader, showLoader, hideLoader } = useLoader();
	const { isOpen, handleOpen, handleClose } = useOpenHandler();
	const [file, setFile] = React.useState<File | null>(null);

	const handleDrop = React.useCallback(
		(acceptedFiles: File[]) => {
			setFile(acceptedFiles[0]);
			handleOpen();
		},
		[handleOpen]
	);

	const handleAccept = React.useCallback(async () => {
		if (file === null) {
			return;
		}
		handleClose();
		showLoader();
		const response = await create(file, importType);
		if (isSuccess(response)) {
			await handleMutate();
			hideLoader();
			toast.success(
				"取込処理を受け付けました。完了までしばらくかかります。ページを再読み込みすると取込履歴に反映されます。"
			);
			return;
		}
		toast.error(response.message ?? "エラーが発生しました。");
		hideLoader();
	}, [file, handleClose, handleMutate, hideLoader, importType, showLoader]);

	const handleCancel = React.useCallback(() => {
		setFile(null);
		handleClose();
	}, [handleClose]);

	return (
		<>
			<Loader />
			<UploadPanel accept=".csv" multiple={false} onDrop={handleDrop} />
			<ConfirmModal
				actionText="開始"
				confirmType="save"
				handleAccept={handleAccept}
				handleCancel={handleCancel}
				isOpen={isOpen}
			>
				取込を開始しますか？
			</ConfirmModal>
		</>
	);
};
